import { useState } from "react";
import { supabase } from "./supabase";
import { seedDefaultTemplates, registerErrorMessage } from "./accountSetup";
import { COUNTRIES, CURRENCIES, regionsFor, presetFor } from "./taxConfig"; // PATCH oct18-tax

// Shown when someone is signed in but no company exists for their login
// (for example an abandoned registration). Creates the company in the database.
export default function FinishSetup({ session, onSignOut }) {
  var user = (session && session.user) || {};
  var meta = user.user_metadata || {};
  var [company, setCompany] = useState("");
  var [name, setName] = useState(meta.full_name || "");
  var [licenseKey, setLicenseKey] = useState("");
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState("");
  var [country, setCountry] = useState("CA"); // PATCH oct18-tax
  var [region, setRegion] = useState("");
  var [currency, setCurrency] = useState("USD");

  async function finish(useKey) {
    if (busy) return;
    setError("");
    var co = company.trim();
    if (!co) { setError("Please enter your company name."); return; }
    if (country !== "OT" && !region) { setError("Please choose your " + (country === "CA" ? "province or territory." : "state.")); return; } // PATCH oct18-tax
    var key = useKey ? licenseKey.trim().toUpperCase() : null;
    if (useKey && !key) { setError("Please enter your license key."); return; }
    setBusy(true);
    try {
      if (key) {
        var chk = await supabase.rpc("check_license_key", { p_key: key });
        if (chk.error) { setError("Could not check the license key. Please try again."); setBusy(false); return; }
        if (chk.data === "invalid") { setError("Invalid license key. Please check and try again."); setBusy(false); return; }
        if (chk.data === "suspended") { setError("This license key has been suspended. Contact support."); setBusy(false); return; }
        if (chk.data === "used") { setError("This license key has already been used."); setBusy(false); return; }
      }
      var res = await supabase.rpc("register_company", { p_company: co, p_name: name.trim(), p_key: key });
      if (res.error || !res.data || !res.data.company_id) {
        setError(registerErrorMessage(res.error));
        setBusy(false);
        return;
      }
      var companyId = res.data.company_id;
      // PATCH oct18-tax: starting tax lines and currency for the chosen country and province or state
      try {
        var _tp = presetFor(country, region || null);
        await supabase.rpc("set_company_tax", { p_company_id: companyId, p_country: country, p_region: region || null, p_lines: _tp.lines, p_mode: "added", p_currency: country === "OT" ? (currency || "USD") : _tp.currency });
      } catch (_) {}
      await seedDefaultTemplates(companyId, co);
      try {
        localStorage.setItem("blindspro_company_id", companyId);
        localStorage.setItem("blindspro_business_name", co);
        localStorage.setItem("businessName", co);
        localStorage.setItem("blindspro_cached_email", String(user.email || "").toLowerCase());
      } catch (_) {}
      window.location.reload();
    } catch (e) {
      setError("Account setup failed. Please try again.");
      setBusy(false);
    }
  }

  // PATCH oct21-ui-login: teal look like the app, dark mode
  var DARK = false;
  try { DARK = localStorage.getItem("darkMode") === "true"; } catch (_) {}
  var T = {
    teal: "#2F6F6B", accent: DARK ? "#7CC2BB" : "#2F6F6B", tint: DARK ? "#1C2E2C" : "#E6F0EF", orange: "#C9620A",
    page: DARK ? "#121615" : "#E2E7E6", /* PATCH oct21-ui-fix2: light page */ card: DARK ? "#1A201F" : "#FFFFFF", text: DARK ? "#E8EEED" : "#1C2B2A",
    sub: DARK ? "#B8C2C1" : "#3A4645", muted: DARK ? "#8E9998" : "#5F6B6A", border: DARK ? "#2C3433" : "#E2E7E6",
    inputBg: DARK ? "#1F2625" : "#FFFFFF", inputBorder: DARK ? "#3A4443" : "#CBD3D2",
    error: DARK ? "#F0776B" : "#B42318", errorBg: DARK ? "#3A1F1D" : "#FBEAE8",
  };
  var IS = { width: "100%", minHeight: 44, padding: "10px 12px", borderRadius: 10, border: "1px solid " + T.inputBorder, fontSize: 15, boxSizing: "border-box", marginBottom: 12, color: T.text, background: T.inputBg, fontFamily: "inherit" };
  var BTN = { width: "100%", minHeight: 46, padding: "0 16px", borderRadius: 10, border: "none", fontSize: 15, fontWeight: 600, cursor: "pointer", marginBottom: 10, fontFamily: "inherit", boxSizing: "border-box" };
  var LBL = { display: "block", fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 };

  return (
    <div style={{ minHeight: "100vh", background: T.page, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, boxSizing: "border-box", fontFamily: "inherit" }}>
      <div style={{ background: T.card, borderRadius: 16, padding: "28px 24px 22px", maxWidth: 400, width: "100%", boxSizing: "border-box", border: "1px solid " + (DARK ? T.border : "#D5DCDB"), boxShadow: DARK ? "0 12px 40px rgba(0,0,0,0.25)" : "0 4px 16px rgba(28,43,42,0.08)" }}>
        <div style={{ width: 64, height: 68, borderRadius: 14, background: T.teal, border: "3px solid #1a1a1a", display: "flex", flexDirection: "column", overflow: "hidden", margin: "0 auto 16px" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 800, color: "white" }}>B</div>
          <div style={{ background: T.orange, padding: "4px 0 8px 0", textAlign: "center", fontSize: 11, fontWeight: 800, color: "white", letterSpacing: 1 }}>PRO</div>
        </div>
        <h2 style={{ textAlign: "center", margin: "0 0 6px", fontSize: 22, fontWeight: 700, color: T.text }}>Finish setting up your account</h2>
        <p style={{ textAlign: "center", color: T.muted, fontSize: 14, margin: "0 0 18px", lineHeight: 1.45 }}>
          You are signed in as <strong style={{ color: T.text }}>{user.email}</strong>, but no company is set up for this login yet.
        </p>
        {error && <div role="alert" style={{ background: T.errorBg, borderRadius: 10, padding: "10px 12px", color: T.error, fontSize: 14, marginBottom: 14 }}>{error}</div>}
        <label htmlFor="fs_company" style={LBL}>Company name</label>
        <input id="fs_company" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} style={IS} />
        {/* PATCH oct18-tax: country and province or state set the starting tax */}
          <label htmlFor="fs_country" style={LBL}>Country</label>
          <select id="fs_country" value={country} onChange={e => { setCountry(e.target.value); setRegion(""); }} style={IS}>
            {COUNTRIES.map(co => <option key={co.code} value={co.code}>{co.name}</option>)}
          </select>
          {regionsFor(country).length > 0 && (
            <>
              <label htmlFor="fs_region" style={LBL}>{country === "CA" ? "Province or territory" : "State"}</label>
              <select id="fs_region" value={region} onChange={e => setRegion(e.target.value)} style={{ ...IS, color: region ? T.text : T.muted }}>
                <option value="">Choose...</option>
                {regionsFor(country).map(rg => <option key={rg.code} value={rg.code}>{rg.name}</option>)}
              </select>
            </>
          )}
          {country === "OT" && (
            <>
              <label htmlFor="fs_currency" style={LBL}>Currency</label>
              <select id="fs_currency" value={currency} onChange={e => setCurrency(e.target.value)} style={IS}>
                {CURRENCIES.map(cu => <option key={cu} value={cu}>{cu}</option>)}
              </select>
            </>
          )}
          <div style={{ fontSize: 13, color: T.muted, marginTop: -4, marginBottom: 14 }}>Sets the tax on your quotes. You can change it later in Settings.</div>
        <label htmlFor="fs_name" style={LBL}>Your name</label>
        <input id="fs_name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} style={IS} />
        <div style={{ background: T.tint, border: "1.5px solid " + T.accent, borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Start a free 15 day trial</div>
          <div style={{ fontSize: 14, color: T.sub, marginBottom: 12 }}>Full Pro features included. No credit card required.</div>
          <button onClick={() => finish(false)} disabled={busy} style={{ ...BTN, background: T.teal, color: "white", marginBottom: 0, opacity: busy ? 0.7 : 1 }}>
            {busy ? "Setting up..." : "Start free trial"}
          </button>
        </div>
        <div style={{ textAlign: "center", color: T.muted, fontSize: 14, margin: "0 0 10px" }}>or enter a license key</div>
        <input aria-label="License key" placeholder="BP-XXXX-XXXX-XXXX" value={licenseKey} onChange={(e) => setLicenseKey(e.target.value.toUpperCase())} style={{ ...IS, textAlign: "center", fontWeight: 600, letterSpacing: 2 }} autoCapitalize="characters" />
        <button onClick={() => finish(true)} disabled={busy} style={{ ...BTN, background: T.card, color: T.accent, border: "1.5px solid " + T.accent, opacity: busy ? 0.7 : 1 }}>
          {busy ? "Activating..." : "Activate with license key"}
        </button>
        <button onClick={onSignOut} disabled={busy} style={{ ...BTN, background: "transparent", color: T.sub, fontSize: 14, marginBottom: 0 }}>Sign out</button>
      </div>
    </div>
  );
}
